import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Image, Platform, ScrollView, View } from 'react-native';

import styles from './definitions-match.style';
import { useColors, useFont } from '../../../hooks/lesson-player.context';
import { useActivityContext } from '../../../hooks/use-activity.context';
import { useScroll } from '../../../hooks/use-scroll';
import { arrayShuffle } from '../../../utils/array';
import trueBeginnerStr from '../../../utils/true-beginner-str';
import { ContinueButton } from '../../continue-button/continue-button';
import { Instruction } from '../../instruction/instruction.component';
import { OptionChooseContainer } from '../../option-choose-container/option-choose-container';
import { ThemedText } from '../../themed-text';
import { ThemedView } from '../../themed-view';

export function DefinitionsMatch() {
  const {
    currentStep,
    currentActivity,
    goNext,
    currentStepIndex,
    completeCurrentStep,
  } = useActivityContext();
  const fonts = useFont();
  const color = useColors();

  const scrollRef = useRef<ScrollView>(null);
  const questionOpacityRef = useRef(new Animated.Value(1));

  const [correctFeedback, setCorrectFeedback] = useState(true);
  const [feedbackTrue, setfeedbackTrue] = useState(false);
  const [showNextButton, setShowNextButton] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [options, setOptions] = useState([]);
  const { scrollToEnd, scrollToTop } = useScroll(scrollRef);

  useEffect(() => {
    setOptions(arrayShuffle(currentActivity.steps.map((x) => x.answer)));
    disableOptions(false);
  }, [currentActivity.steps, currentStepIndex]);

  const [optionsAreDisabled, disableOptions] = useState(false);

  const onAnswer = (isCorrect: boolean) => {
    disableOptions(true);
    setTimeout(() => {
      if (isCorrect) {
        setfeedbackTrue(true);
        setOptions(options.filter((x) => x !== currentStep.answer));
      }
      setCorrectFeedback(isCorrect);
      completeCurrentStep(isCorrect);
      setShowNextButton(true);
      setAttempt((i) => i + 1);
      scrollToEnd();
    }, 800);
  };

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, currentStepIndex]);

  const resetAndGoNextStep = async () => {
    setShowNextButton(false);
    await questionFade(false);
    disableOptions(false);
    goNext();
    setfeedbackTrue(false);
    questionFade(true);
  };

  const questionFade = useCallback((show: boolean) => {
    return new Promise((resolve) => {
      Animated.timing(questionOpacityRef.current, {
        toValue: show ? 1 : 0,
        duration: 400,
        useNativeDriver: Platform.OS !== 'web',
      }).start(resolve);
    });
  }, []);

  return (
    <>
      <ScrollView ref={scrollRef} contentContainerStyle={styles.container}>
        <View style={styles.content}>
          <Instruction
            text={trueBeginnerStr(
              currentActivity,
              'instructions',
              currentActivity.trueBeginner
            )}
          />
          {!!currentStep?.image && (
            <Image style={styles.image} source={{ uri: currentStep?.image }} />
          )}

          <ThemedView style={[styles.view]}>
            {!feedbackTrue && (
              <Animated.View
                style={[
                  styles.dashedBoxContainer,
                  { opacity: questionOpacityRef.current },
                ]}
              >
                <View
                  style={[
                    styles.dashedBox,
                    {
                      borderColor: color.onPrimary,
                      backgroundColor: color.primaryOpacity,
                    },
                  ]}
                />
              </Animated.View>
            )}
            {feedbackTrue && (
              <Animated.View
                style={[styles.answer, { opacity: questionOpacityRef.current }]}
              >
                <ThemedText
                  type="onPrimary"
                  fontSize="medium"
                  style={[{ ...fonts.primary.bold }]}
                >
                  {currentStep?.answer}
                </ThemedText>
              </Animated.View>
            )}
            <ThemedText
              type="onPrimary"
              fontSize="small"
              style={[styles.text, { ...fonts.primary.regular }]}
            >
              {trueBeginnerStr(
                currentStep,
                'text',
                currentActivity.trueBeginner
              )}
            </ThemedText>
          </ThemedView>
        </View>
        <OptionChooseContainer
          containerStyle={styles.optionContainer}
          disabled={optionsAreDisabled}
          answer={currentStep?.answer}
          options={options}
          onAnswer={onAnswer}
          index={attempt}
          checkButtonActive={Platform.OS !== 'web'}
          scrollToEnd={scrollToEnd}
        />
      </ScrollView>
      {showNextButton && (
        <ContinueButton
          feedbackTitle={currentStep?.feedbackTitle}
          feedback={currentStep?.feedback}
          isCorrect={correctFeedback}
          onPress={resetAndGoNextStep}
        />
      )}
    </>
  );
}
